import React, { useState, useRef, useCallback, useEffect } from 'react';
import { loadTable, saveRow, isApiReady, checkApi, getTableData, saveTableData, getNextId } from '../../db-viewer/data/dataAccess';
import { confirmDiscardChanges } from '../../../lib/unsavedChanges';
import { getMaxUploadMb, DEFAULT_MAX_UPLOAD_MB, getIncompleteUploads, IncompleteUpload } from '../../../lib/serverDocStore';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import { ExtractEngine } from '../utils/documentExtract';
import {
  UploadJob, UploadContext, useUploadJobs, addUploadJobs, removeUploadJob, clearUploadJobs,
  getFileExtension, IMAGE_EXTENSIONS, subscribeDocsUploaded, confirmUploadSplit, retryUploadJob,
  jobInWindow, UploadMode,
} from '../utils/uploadQueue';
import SplitReviewModal from './SplitReviewModal';
import CaptureScreenModal, { canCaptureScreen, grabScreenFrame } from './CaptureScreenModal';
import ConvertToCsvTab from './ConvertToCsvTab';
import EmailInboxTab from './EmailInboxTab';
import { EMAIL_EXTENSIONS } from '../../../lib/emailStore';
import { getClientEntities } from '../utils/ownerEntities';
import './UploadDocsPage.css';

// =============================================
// Types
// =============================================
type UploadTabKey = 'receipts-docs' | 'receipts-separate' | 'receipts-auto' | 'convert-csv' | 'email-in';

// A picked file and its progress — owned by the app-wide upload queue so it
// keeps processing after this page is left.
type UploadedFile = UploadJob;

interface ClientOption {
  id: number;
  name: string;
  // 'client' / 'client_business' = the selected client's own books (Client's Accounts).
  type: 'personal' | 'family' | 'business' | 'client' | 'client_business';
  ownerId: number | null;
}

// =============================================
// Constants
// =============================================
const TABS: { key: UploadTabKey; label: string; disabled?: boolean }[] = [
  { key: 'receipts-docs',     label: 'Upload Receipts / Docs' },
  { key: 'receipts-separate', label: 'Separate & Upload Receipts / Docs' },
  { key: 'receipts-auto',     label: 'Auto-Separate Mixed Receipts / Docs' },
  { key: 'convert-csv',       label: 'Upload to Convert to .csv file' },
  { key: 'email-in',          label: 'Forward Emails to File' },
];

// Emails (.eml / .msg) are accepted too: the message and its PDF/image attachments
// each become a document (read on the server — see lib/emailStore.ts).
const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', ...EMAIL_EXTENSIONS];
const CAN_CAPTURE = canCaptureScreen();
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const ACCEPT_INPUT ='.pdf,.png,.jpg,.jpeg,.eml,.msg,application/pdf,image/png,image/jpeg,message/rfc822,application/vnd.ms-outlook';

const VALID_MIME_TYPES: Record<string, string[]> = {
  '.pdf':  ['application/pdf'],
  '.png':  ['image/png'],
  '.jpg':  ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  // .eml / .msg: browsers report these inconsistently (often nothing) — not checked.
};

const EXPECTED_FORMAT: Record<string, string> = {
  '.pdf': 'pdf', '.png': 'png', '.jpg': 'jpeg', '.jpeg': 'jpeg', '.eml': 'eml', '.msg': 'msg',
};

const TAB_INFO: Record<UploadTabKey, { title: string; description: string; hint: string; acceptLabel: string }> = {
  'receipts-docs': {
    title: 'Upload Receipts / Docs',
    description: 'Upload PDF or image files with either a single page receipt or a multiple page receipt. We won\'t separate pages in uploaded file as individual receipts.',
    hint: 'Supported formats: PDF, PNG, JPEG/JPG. Image files are automatically converted to PDF before storage.\nEmails saved from your mail program (.eml or Outlook .msg) can be dropped here too: the email is saved as one document — the message first, then its PDF and image attachments as the following pages.\nIf your file contains multiple receipts or docs then use the "Separate & Upload" tab to split each page into its own receipt, or the "Auto-Separate" tab when some of them run over several pages.\nExamples of single page receipt is a receipt for gas filled for car. In this case, receipt starts and ends on one page.\nExample of one multi-page document will be a grocery invoice for a restaurant where 100s of items are received and the invoice will run over multiple pages.',
    acceptLabel: 'PDF, PNG, JPEG, EML, MSG',
  },
  'receipts-separate': {
    title: 'Separate & Upload Receipts / Docs',
    description: 'Upload a PDF or image — each page of a PDF will be separated and saved as an individual document for processing.',
    hint: 'Use this when you have scanned multiple receipts into one PDF. Each page will be split into its own receipt during processing.\nFor example, if you scan 10 gas receipts into one PDF, this will create 10 individual receipts.\nImage files (PNG, JPEG) are converted to a single-page PDF. Supported formats: PDF, PNG, JPEG/JPG, and emails (.eml / .msg — every page of the message and of each attachment becomes its own document).',
    acceptLabel: 'PDF, PNG, JPEG, EML, MSG',
  },
  'receipts-auto': {
    title: 'Auto-Separate Mixed Receipts / Docs',
    description: 'Upload one PDF that holds several receipts and documents — some one page, some running over several pages. Each page is read, and the pages that belong together are saved as one document.',
    hint: 'Use this when a scanned batch mixes single-page receipts with multi-page invoices or statements. For example: 3 gas receipts and a 4-page grocery invoice scanned into one 7-page PDF become 4 documents.\nPages are matched by what is printed on them: "Page 2 of 4", the same invoice number, "continued" wording, totals and the page size. Scanned pages are read with OCR first, so a long scan takes a little while.\nYou see the suggested split on page thumbnails and can correct it before anything is saved. Places the reader was unsure of are marked "Check".\nImage files (PNG, JPEG) are saved as one single-page document. For an email (.eml / .msg), the message and each attachment are separated the same way, without the check step.',
    acceptLabel: 'PDF, PNG, JPEG, EML, MSG',
  },
  'convert-csv': {
    title: 'Upload to Convert to .csv file',
    description: 'Upload a bank or credit-card statement (PDF, PNG, or JPEG) — the transactions are extracted into an editable table you can review, export as CSV, and import into bookkeeping.',
    hint: 'Pick an extraction engine below. Free · Browser costs nothing (digital PDFs are read instantly; photos/scans use slower OCR). Claude API gives the best accuracy on scans/photos. Review the rows before importing.',
    acceptLabel: 'PDF, PNG, JPEG',
  },
  'email-in': {
    title: 'Forward Emails to File',
    description: "A private email address — yours, or under Client's Accounts the client's own: forward a receipt or invoice to it from any mail program or phone, and it is waiting in Process Docs / Receipts.",
    hint: 'Each email is saved as one document: the message first, then its PDF and image attachments as the following pages.\nTo file an email you have saved as a file (.eml or .msg), drop it on the "Upload Receipts / Docs" tab instead.',
    acceptLabel: '',
  },
};

// Per-file cap = this login's limit (plan + per-user override, migration 112), asked
// from the server on mount. Until it answers — or offline — the default applies.
const MB = 1024 * 1024;

// =============================================
// Utility Functions
// =============================================
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function nowTimestamp(): string {
  return new Date().toISOString().replace('T', ' ').substring(0, 19);
}

// =============================================
// File Validation — magic bytes
// =============================================
async function validateFileHeader(file: File): Promise<{ valid: boolean; format: string | null }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arr = new Uint8Array(reader.result as ArrayBuffer);
      if (arr.length < 3) { resolve({ valid: false, format: null }); return; }

      // Outlook .msg: an OLE compound file — D0 CF 11 E0 A1 B1 1A E1
      if (arr.length >= 8 &&
          arr[0] === 0xD0 && arr[1] === 0xCF && arr[2] === 0x11 && arr[3] === 0xE0 &&
          arr[4] === 0xA1 && arr[5] === 0xB1 && arr[6] === 0x1A && arr[7] === 0xE1) {
        resolve({ valid: true, format: 'msg' }); return;
      }

      // PDF: %PDF
      if (arr.length >= 4 &&
          arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46) {
        resolve({ valid: true, format: 'pdf' }); return;
      }
      // PNG: 89 50 4E 47
      if (arr.length >= 4 &&
          arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47) {
        resolve({ valid: true, format: 'png' }); return;
      }
      // JPEG: FF D8 FF
      if (arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF) {
        resolve({ valid: true, format: 'jpeg' }); return;
      }

      // .eml: plain text that opens with header lines ("Received: …", "From: …").
      // A loose check only — the server reads the email properly and says if it can't.
      let head = '';
      for (let i = 0; i < arr.length; i++) head += String.fromCharCode(arr[i]);
      const headerLines = head.split('\n').filter(line => /^[A-Za-z][A-Za-z0-9-]*: ?[^ ]/.test(line));
      if (headerLines.length >= 2) {
        resolve({ valid: true, format: 'eml' }); return;
      }

      resolve({ valid: false, format: null });
    };
    reader.onerror = () => resolve({ valid: false, format: null });
    reader.readAsArrayBuffer(file.slice(0, 4096));
  });
}

// =============================================
// Client / User helpers
// =============================================
function getCurrentUserFromData(usersData: Record<string, any>[]): { user_id: number; owner_id: number | null; role: string } | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null ? null : Number(userRow.owner_id),
      role: session.role || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getClientsFromData(
  usersData: Record<string, any>[],
  businessesData: Record<string, any>[],
  currentUser: { user_id: number; owner_id: number | null; role: string }
): ClientOption[] {
  const clients: ClientOption[] = [];
  const accountOwnerId = currentUser.owner_id == null
    ? currentUser.user_id
    : currentUser.owner_id;

  try {
    for (const u of usersData) {
      if (u.is_deleted === 1) continue;
      const uid = Number(u.user_id);
      const oid = u.owner_id == null ? null : Number(u.owner_id);
      if (uid !== accountOwnerId && oid !== accountOwnerId) continue;
      // Personal Clients belong to Client's Accounts, not to the account's own family.
      if (u.relationship === 'Personal Client') continue;
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.user_id}`;
      const isOwner = oid == null;
      clients.push({ id: uid, name, type: isOwner ? 'personal' : 'family', ownerId: oid });
    }
  } catch { /* ignore */ }
  try {
    for (const b of businessesData) {
      if (b.is_deleted === 1) continue;
      if (Number(b.owner_id) !== accountOwnerId) continue;
      const name = b.busi_legal_name || b.busi_operating_name || `Business #${b.business_id}`;
      clients.push({ id: b.business_id + 10000, name, type: 'business', ownerId: b.owner_id == null ? null : Number(b.owner_id) });
    }
  } catch { /* ignore */ }
  return clients;
}

// Client's Accounts: the pickable owners are the selected client, the client's
// family and the client's businesses — never the account owner's own entities.
function getClientOwnerOptions(
  clientId: number | string,
  usersData: Record<string, any>[],
  businessesData: Record<string, any>[],
): ClientOption[] {
  return getClientEntities(clientId, usersData, businessesData).map(e => (
    e.owner_type === 'client_business'
      ? { id: e.owner_id + 10000, name: e.display_name, type: 'client_business' as const, ownerId: Number(clientId) }
      : { id: e.owner_id, name: e.display_name, type: 'client' as const, ownerId: Number(clientId) }
  ));
}

// =============================================
// Main Component
// =============================================
interface UploadDocsPageProps {
  /** 'owner' = DMS / Your Accounts (own + family + businesses); 'clients' = Client's Accounts. */
  viewMode?: 'owner' | 'clients';
  /** The client being managed (Client's Accounts only). */
  selectedClientId?: number | string | null;
}

const UploadDocsPage: React.FC<UploadDocsPageProps> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const clientMode = viewMode === 'clients';
  const [activeTab, setActiveTab] = useState<UploadTabKey>(() => {
    const saved = sessionStorage.getItem('akt_upload_active_tab');
    if (saved === 'receipts-docs' || saved === 'receipts-separate' || saved === 'receipts-auto' || saved === 'convert-csv') return saved;
    if (saved === 'email-in') return saved;
    return 'receipts-docs';
  });
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const allFiles = useUploadJobs();
  // This upload window = the tab + the screen it sits on (DMS / Your Accounts /
  // Client's Accounts, and which client). Each window lists only its own files.
  const uploadOrigin = `${window.location.pathname}|${clientMode ? selectedClientId ?? '' : ''}`;
  const files = React.useMemo(
    () => allFiles.filter(j => jobInWindow(j, activeTab as UploadMode, uploadOrigin)),
    [allFiles, activeTab, uploadOrigin],
  );
  const [isDragging, setIsDragging] = useState(false);
  const [maxFileSize, setMaxFileSize] = useState(DEFAULT_MAX_UPLOAD_MB * MB);
  useEffect(() => {
    let cancelled = false;
    getMaxUploadMb().then(mb => { if (!cancelled) setMaxFileSize(mb * MB); });
    return () => { cancelled = true; };
  }, []);
  const [notes, setNotes] = useState('');
  const [showFirstTimeHint, setShowFirstTimeHint] = useState(true);
  const [documents, setDocuments] = useState<Record<string, any>[]>([]);
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [docuTypes, setDocuTypes] = useState<Record<string, any>[]>([]);
  // Engine used to auto-fill the Filing fields on upload. Default 'auto' = read
  // everything free in-browser first and escalate only the weak results to AI
  // (cost-optimal). Persisted under its own key (separate from the Convert-to-CSV
  // tab's akt_extract_engine, which only knows local/claude).
  const [engine, setEngine] = useState<ExtractEngine>(() => {
    try {
      const saved = localStorage.getItem('akt_doc_extract_engine');
      if (saved === 'auto' || saved === 'local' || saved === 'claude' || saved === 'self_hosted') return saved;
    } catch { /* ignore */ }
    return 'auto';
  });
  const changeEngine = useCallback((e: ExtractEngine) => {
    setEngine(e);
    try { localStorage.setItem('akt_doc_extract_engine', e); } catch { /* ignore */ }
  }, []);
  // Auto-separate: hold each file for a look at the suggested split before saving
  // (default on). Off = save the suggested split straight away.
  const [reviewSplit, setReviewSplit] = useState<boolean>(() => {
    try { return localStorage.getItem('akt_upload_review_split') !== '0'; } catch { return true; }
  });
  const changeReviewSplit = useCallback((on: boolean) => {
    setReviewSplit(on);
    try { localStorage.setItem('akt_upload_review_split', on ? '1' : '0'); } catch { /* ignore */ }
  }, []);
  // The file whose suggested split is open in the check dialog (null = closed).
  const [reviewJobId, setReviewJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCountRef = useRef(0);
  // Sort for the "Uploaded Documents" history table (default: newest upload first).
  const [sortField, setSortField] = useState<'filename' | 'page_count' | 'upload_date'>('upload_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Delete-confirmation modal target (null = closed). childDocs = separated pages
  // that cascade-delete with an original.
  const [deleteTarget, setDeleteTarget] = useState<{ doc: any; childDocs: any[] } | null>(null);

  // Load all data from API (or localStorage fallback) on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await checkApi();
      const [users, businesses, docs, dtypes] = await Promise.all([
        loadTable('users'),
        loadTable('user_businesses'),
        loadTable('dms_documents'),
        loadTable('acctg_docu_type'),
      ]);
      if (cancelled) return;
      setUsersData(users);
      setBusinessesData(businesses);
      setDocuments(docs);
      setDocuTypes(dtypes);
      if (clientMode) {
        // Uploads here belong to the client being managed: preselect the client so the
        // documents land in that client's Process Docs (which lists only its own).
        setClients(selectedClientId != null ? getClientOwnerOptions(selectedClientId, users, businesses) : []);
        setSelectedClient(selectedClientId != null ? Number(selectedClientId) : null);
      } else {
        const cu = getCurrentUserFromData(users);
        if (cu) {
          setClients(getClientsFromData(users, businesses, cu));
        }
      }
      setDataLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [clientMode, selectedClientId]);

  const tabInfo = TAB_INFO[activeTab];
  const isDisabledTab = TABS.find(t => t.key === activeTab)?.disabled;

  // Reload documents when tab changes
  useEffect(() => {
    sessionStorage.setItem('akt_upload_active_tab', activeTab);
    if (dataLoaded) {
      (async () => {
        const docs = await loadTable('dms_documents');
        setDocuments(docs);
      })();
    }
  }, [activeTab, dataLoaded]);

  // ---- Drag & Drop ----
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCountRef.current++;
    if (dragCountRef.current === 1) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCountRef.current--;
    if (dragCountRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
  }, []);

  // ---- Upload context: who/how the files being added right now should be saved ----
  // Captured per file when it is added, so the background queue doesn't depend on
  // this page staying open (or on what is selected here later).
  const buildUploadContext = useCallback((): UploadContext => {
    const currentUser = getCurrentUserFromData(usersData);
    const accountOwnerId = currentUser?.owner_id ?? currentUser?.user_id ?? 1;
    const docUserId = selectedClient && selectedClient < 10000
      ? selectedClient
      : currentUser?.user_id ?? 1;

    // Resolve owner_type + owner_id from selectedClient for pre-populating Process Receipts
    let docOwnerType: string | null = null;
    let docOwnerId: number | null = null;
    if (selectedClient) {
      const chosen = clients.find(c => c.id === selectedClient);
      if (chosen) {
        if (chosen.type === 'personal') {
          docOwnerType = 'account_owner';
          docOwnerId = chosen.id;
        } else if (chosen.type === 'family') {
          docOwnerType = 'family_member';
          docOwnerId = chosen.id;
        } else if (chosen.type === 'business') {
          docOwnerType = 'business';
          docOwnerId = chosen.id - 10000; // undo the offset from getClients()
        } else if (chosen.type === 'client') {
          docOwnerType = 'client_personal';
          docOwnerId = chosen.id;
        } else if (chosen.type === 'client_business') {
          docOwnerType = 'client_business';
          docOwnerId = chosen.id - 10000;
        }
      }
    }

    return {
      mode: activeTab === 'receipts-separate' || activeTab === 'receipts-auto' ? activeTab : 'receipts-docs',
      reviewSplit,
      engine,
      accountOwnerId,
      docUserId,
      docOwnerType,
      docOwnerId,
      createdBy: currentUser?.user_id ?? null,
      notes: notes || null,
      docuTypes,
      origin: uploadOrigin,
    };
  }, [selectedClient, clients, activeTab, notes, usersData, engine, docuTypes, reviewSplit, uploadOrigin]);

  // ---- File Validation ----
  const validateAndAddFiles = useCallback(async (fileList: FileList | File[]) => {
    const ctx = buildUploadContext();   // as selected at the moment of the drop / pick
    const newFiles: UploadedFile[] = [];
    const len = fileList instanceof FileList ? fileList.length : fileList.length;

    for (let i = 0; i < len; i++) {
      const file = fileList instanceof FileList ? fileList.item(i) : fileList[i];
      if (!file) continue;

      const ext = getFileExtension(file.name);

      // Extension check
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `Unsupported format (${ext}). Accepted: PDF, PNG, JPEG/JPG, or an email (.eml / .msg).`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      // MIME type check
      if (file.type) {
        const validMimes = VALID_MIME_TYPES[ext] || [];
        if (validMimes.length > 0 && !validMimes.includes(file.type)) {
          newFiles.push({
            id: generateId(), file, name: file.name, size: file.size, type: file.type,
            status: 'error', progress: 0,
            errorMsg: `MIME type mismatch (${file.type} for ${ext}). File may be renamed.`,
            addedAt: new Date().toISOString(),
          });
          continue;
        }
      }

      // Size check
      if (file.size > maxFileSize) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `File too large (${formatFileSize(file.size)}). Max ${formatFileSize(maxFileSize)} per file.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      // Magic bytes check
      const { valid, format } = await validateFileHeader(file);
      if (!valid) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `File header check failed — not a valid ${ext.substring(1).toUpperCase()} file.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      // Cross-check: magic bytes format should match extension
      if (format !== EXPECTED_FORMAT[ext]) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `File content is ${format?.toUpperCase()} but extension is ${ext}. Please rename correctly.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      const isImage = IMAGE_EXTENSIONS.includes(ext);
      newFiles.push({
        id: generateId(), file, name: file.name, size: file.size, type: file.type,
        status: 'queued', progress: 0, addedAt: new Date().toISOString(),
        convertedFrom: isImage ? ext.substring(1).toUpperCase() : EMAIL_EXTENSIONS.includes(ext) ? 'Email' : undefined,
        ctx,
      });
    }

    // Rejected files carry no ctx — tag them with this window so they list here only.
    newFiles.forEach(f => { if (!f.ctx) f.source = { mode: ctx.mode, origin: ctx.origin }; });

    // Hand off to the background queue — valid files start uploading straight away
    // and keep going if the user leaves this page.
    addUploadJobs(newFiles);
  }, [buildUploadContext, maxFileSize]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCountRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      validateAndAddFiles(e.dataTransfer.files);
    }
  }, [validateAndAddFiles]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Snapshot to a real array BEFORE clearing the input. e.target.files is a
      // live FileList — clearing e.target.value empties it, and since
      // validateAndAddFiles awaits (header checks) it would then read every file
      // after the first as null. Array.from() detaches a stable copy.
      const picked = Array.from(e.target.files);
      e.target.value = '';
      validateAndAddFiles(picked);
    }
  }, [validateAndAddFiles]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // A screenshot (pasted or captured) gets a dated name so documents stay apart,
  // and the on-page message below follows its upload.
  const addScreenshots = useCallback((shots: File[]) => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    const named = shots.map((f, i) => {
      if (!/^image\.(png|jpe?g)$/i.test(f.name) && f.name) return f;   // a real file name is kept
      const ext = f.type === 'image/jpeg' ? 'jpg' : 'png';
      const suffix = shots.length > 1 ? `_${i + 1}` : '';
      return new File([f], `screenshot_${stamp}${suffix}.${ext}`, { type: f.type || 'image/png', lastModified: d.getTime() });
    });
    setPastedNames(named.map(f => f.name));
    validateAndAddFiles(named);
  }, [validateAndAddFiles]);

  // "Capture screen": the browser's share picker → one frame → crop modal.
  const [captureFrame, setCaptureFrame] = useState<HTMLCanvasElement | null>(null);
  const startCapture = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();   // the button sits in the drop zone, whose click opens Browse
    setCaptureFrame(null);
    const frame = await grabScreenFrame();
    if (frame) setCaptureFrame(frame);
  }, []);
  const keepCapture = useCallback((blob: Blob) => {
    setCaptureFrame(null);
    addScreenshots([new File([blob], 'image.png', { type: 'image/png' })]);
  }, [addScreenshots]);

  // Ctrl+V anywhere on the page uploads what is on the clipboard — a screenshot
  // (Win+Shift+S / Cmd+Ctrl+Shift+4) or files copied in Explorer. Only while the
  // drop zone is shown, and never while typing in a box (that paste is text).
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!fileInputRef.current) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const items = Array.from(e.clipboardData?.items || []);
      const pasted = items.filter(it => it.kind === 'file').map(it => it.getAsFile()).filter((f): f is File => !!f);
      if (pasted.length === 0) return;
      e.preventDefault();
      addScreenshots(pasted);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [addScreenshots]);

  // On-page message for a paste (not a browser pop-up, so pop-up blockers don't
  // touch it): "uploading…" → "uploaded" / the error. X or a click anywhere closes it.
  const [pastedNames, setPastedNames] = useState<string[]>([]);
  const pastedJobs = pastedNames.length
    ? allFiles.filter(j => pastedNames.includes(j.name)).slice(-pastedNames.length)
    : [];
  const pasteState: 'uploading' | 'done' | 'error' | null = !pastedJobs.length ? null
    : pastedJobs.some(j => j.status === 'error') ? 'error'
    : pastedJobs.every(j => j.status === 'done') ? 'done' : 'uploading';
  useEffect(() => {
    if (!pastedNames.length) return;
    const close = () => setPastedNames([]);
    // Added on the next tick so the paste's own click/keys don't close it at once.
    const t = window.setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [pastedNames]);

  const removeFile = useCallback((id: string) => {
    removeUploadJob(id);
  }, []);

  // Clears only this window's files — other tabs' / screens' uploads stay listed there.
  const clearAllFiles = useCallback(() => {
    clearUploadJobs(j => jobInWindow(j, activeTab as UploadMode, uploadOrigin));
  }, [activeTab, uploadOrigin]);

  // Open the delete-confirmation modal for a document. For an original in the
  // separate-upload tab, gather the separated pages that will cascade-delete.
  const requestDelete = useCallback((doc: any) => {
    // A virtual original header (multi-page upload) has no real row — its
    // "children" are ALL the page rows sharing the original filename.
    if (doc.__virtual) {
      const childDocs = documents.filter((d: any) =>
        d.original_filename === doc.original_filename &&
        d.upload_source === 'receipts-separate' &&
        d.is_deleted !== 1
      );
      setDeleteTarget({ doc, childDocs });
      return;
    }
    const isOriginal = doc.upload_source === 'receipts-separate' &&
      doc.filename === doc.original_filename;
    const childDocs = isOriginal
      ? documents.filter((d: any) =>
          d.document_id !== doc.document_id &&
          d.original_filename === doc.original_filename &&
          d.upload_source === 'receipts-separate' &&
          d.account_owner_id === doc.account_owner_id &&
          d.is_deleted !== 1
        )
      : [];
    setDeleteTarget({ doc, childDocs });
  }, [documents]);

  // Soft-delete the modal's target (cascades to separated pages for an original).
  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { doc, childDocs } = deleteTarget;
    // A virtual header has no real document_id — only its page rows get deleted.
    const baseIds = doc.__virtual ? [] : [doc.document_id];
    const idsToDelete = [...baseIds, ...childDocs.map((d: any) => d.document_id)];
    const now = nowTimestamp();

    if (isApiReady()) {
      await Promise.all(
        idsToDelete.map(id =>
          saveRow('dms_documents', id, { is_deleted: 1, status: 'Deleted', updated_at: now }, 'document_id')
        )
      );
      const updatedDocs = await loadTable('dms_documents');
      setDocuments(updatedDocs);
    } else {
      const allDocs = await loadTable('dms_documents');
      const idsSet = new Set(idsToDelete);
      const updated = allDocs.map((d: any) =>
        idsSet.has(d.document_id) ? { ...d, is_deleted: 1, status: 'Deleted', updated_at: now } : d
      );
      saveTableData('dms_documents', updated);
      setDocuments(updated);
    }
    setDeleteTarget(null);
  }, [deleteTarget]);

  // Navigate to Process Docs / Receipts with a document selected
  const goToProcessDoc = useCallback((docId: number) => {
    sessionStorage.setItem('akt_navigate_to_doc', String(docId));
    window.location.hash = 'process-receipts';
  }, []);

  // The background queue finished a file (possibly one added before this page was
  // last left) → refresh the "Uploaded Documents" list.
  useEffect(() => {
    let cancelled = false;
    const onUploaded = async () => {
      const docs = await loadTable('dms_documents');
      if (!cancelled) setDocuments(docs);
    };
    const unsubscribe = subscribeDocsUploaded(onUploaded);
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  // Files the server holds only part of (an upload that was cut short on any device).
  // Asked on opening the page and again whenever a file finishes.
  const [incomplete, setIncomplete] = useState<IncompleteUpload[]>([]);
  const incompleteUser = getCurrentUserFromData(usersData);
  const incompleteAccountId = incompleteUser?.owner_id ?? incompleteUser?.user_id ?? null;
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const list = await getIncompleteUploads(incompleteAccountId);
      if (!cancelled) setIncomplete(list);
    };
    void refresh();
    const unsubscribe = subscribeDocsUploaded(refresh);
    return () => { cancelled = true; unsubscribe(); };
  }, [incompleteAccountId]);

  const queuedCount = files.filter(f => f.status === 'queued').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const doneCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const reviewCount = files.filter(f => f.status === 'review').length;
  // Keep the file being uploaded right now in view: when the queue moves on to the
  // next file, the list scrolls to it (only the list — the page itself stays put).
  const fileListRef = useRef<HTMLDivElement>(null);
  const currentJobId = (files.find(f => f.status === 'uploading') || files.find(f => f.status === 'queued'))?.id;
  useEffect(() => {
    const list = fileListRef.current;
    if (!list || !currentJobId) return;
    const item = list.querySelector<HTMLElement>(`[data-job-id="${currentJobId}"]`);
    if (!item) return;
    const top = item.offsetTop;
    if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: Math.max(0, top - (list.clientHeight - item.offsetHeight) / 2), behavior: 'smooth' });
    }
  }, [currentJobId]);
  const reviewJob = reviewJobId ? files.find(f => f.id === reviewJobId && f.status === 'review') : undefined;

  // Documents for current tab (not deleted)
  // For separate-upload tab: group originals on top with their pages below
  // A doc stays in the Upload window only while it's still pending — i.e. as
  // uploaded (status 'Review') and not yet filed into a folder. Once it's
  // changed in Process Docs / Receipts (filed / rejected / BK done / moved) it
  // disappears from here and lives in Process Receipts.
  const isPendingDoc = useCallback((d: any) => {
    // Pending = untouched since upload. Any edit in Process Docs/Receipts (file,
    // reject, BK-done, move, set owner) bumps updated_at, dropping it from here.
    if (d.created_at && d.updated_at) return d.updated_at === d.created_at;
    // Fallback when timestamps are missing: as-uploaded status, not yet filed.
    return d.status === 'Review' && (d.dms_folders_id == null || Number(d.dms_folders_id) === 0);
  }, []);

  // Sort comparator value for a doc on the active sort field.
  const sortValueOf = useCallback((d: any) => {
    switch (sortField) {
      case 'filename': return (d.filename || '').toLowerCase();
      case 'page_count': return Number(d.page_count) || 0;
      case 'upload_date':
      default: return d.upload_date || '';
    }
  }, [sortField]);

  const toggleSort = useCallback((field: typeof sortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  // Arrow indicator in a sortable column header: active → direction, else a
  // neutral hint that the column can be sorted.
  const sortArrow = (field: typeof sortField) => (
    <span className={`ud-sort-arrow ${sortField === field ? 'active' : ''}`}>
      {sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
    </span>
  );

  const tabDocs = React.useMemo(() => {
    // Each pillar lists only its own pending uploads: Client's Accounts shows the
    // managed client's documents; DMS / Your Accounts never show client documents.
    const isClientDoc = (d: any) => d.owner_type === 'client_personal' || d.owner_type === 'client_business';
    const inPillar = (d: any) => {
      if (!clientMode) return !isClientDoc(d);
      return clients.some(c => (
        c.type === 'client_business'
          // eslint-disable-next-line eqeqeq
          ? d.owner_type === 'client_business' && d.owner_id == c.id - 10000
          // eslint-disable-next-line eqeqeq
          : d.owner_type === 'client_personal' && d.owner_id == c.id
      ));
    };
    const docs = documents.filter(d =>
      d.upload_source === activeTab && d.is_deleted !== 1 && isPendingDoc(d) && inPillar(d)
    );
    const cmp = (a: any, b: any) => {
      const av = sortValueOf(a), bv = sortValueOf(b);
      let r = 0;
      if (av < bv) r = -1; else if (av > bv) r = 1;
      return sortDir === 'asc' ? r : -r;
    };

    if (activeTab !== 'receipts-separate') {
      return [...docs].sort(cmp);
    }

    // Separate-upload: a multi-page upload is saved ONLY as page rows (no real
    // original row). Synthesize a virtual "original" header per group so the user
    // can see and delete the whole document at once (cascading to its pages). A
    // single-page upload already has a real original row (filename === original).
    const extractPageNum = (filename: string): number => {
      const m = filename.match(/_page(\d+)\.pdf$/i);
      return m ? parseInt(m[1], 10) : 0;
    };
    const groups = new Map<string, any[]>();
    docs.forEach(d => {
      const key = d.original_filename || d.filename;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(d);
    });
    const groupList = Array.from(groups.entries()).map(([key, group]) => {
      const realOriginal = group.find(d => d.filename === d.original_filename);
      const pages = group
        .filter(d => d.filename !== d.original_filename)
        .sort((a, b) => extractPageNum(a.filename) - extractPageNum(b.filename));
      let header: any = realOriginal || null;
      if (!header && pages.length > 0) {
        header = {
          __virtual: true,
          document_id: `virt:${key}`,
          filename: key,
          original_filename: key,
          upload_date: pages[0].upload_date,
          created_at: pages[0].created_at,
          page_count: pages.length,
          account_owner_id: pages[0].account_owner_id,
          notes: null,
        };
      }
      return { header, pages, sortDoc: header || pages[0] };
    });
    groupList.sort((a, b) => cmp(a.sortDoc, b.sortDoc));
    const rows: any[] = [];
    groupList.forEach(g => {
      if (g.header) rows.push(g.header);
      rows.push(...g.pages);
    });
    return rows;
  }, [documents, activeTab, isPendingDoc, sortValueOf, sortDir, clientMode, clients]);

  return (
    <div className="ud-page">
      {/* Page Banner */}
      <div className="ud-page-banner">
        <p><strong>To Manage All Of Your Invoices, Expense Receipts, And All Documents Relating To All Of Your Family, Businesses And Your Clients</strong></p>
        <p><strong>UPLOAD Your Files Below</strong></p>
      </div>

      {/* Tab Bar */}
      <div className="ud-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`ud-tab-btn ${activeTab === tab.key ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
            onClick={async () => {
              if (tab.disabled) return;
              if (!(await confirmDiscardChanges())) return;
              setActiveTab(tab.key);
            }}
            title={tab.disabled ? 'Coming Soon' : undefined}
          >
            {tab.label}
            {tab.disabled && <span className="ud-tab-coming-soon">Coming Soon</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="ud-tab-content">
        {/* Title & Description */}
        <h2 className="ud-title">{tabInfo.title}</h2>
        <p className="ud-description">{tabInfo.description}</p>

        {/* Hint text */}
        <div className="ud-hint-block">
          {tabInfo.hint.split('\n').map((line, i) => (
            <p key={i} className="ud-hint-line">{line}</p>
          ))}
        </div>

        {/* Convert to CSV — bank statement extraction (Claude vision) */}
        {activeTab === 'convert-csv' ? (
          <ConvertToCsvTab clients={clients} />
        ) : activeTab === 'email-in' ? (
          // Under Client's Accounts: the managed client's own address (filed into their documents).
          clientMode && selectedClientId == null ? (
            <p className="ud-description">Choose a client first.</p>
          ) : (
            <EmailInboxTab
              key={clientMode ? `client-${selectedClientId}` : 'own'}
              owners={clients}
              onOpenDocument={goToProcessDoc}
              clientId={clientMode ? Number(selectedClientId) : null}
              clientName={clientMode ? (clients.find(c => c.type === 'client' && c.id === Number(selectedClientId))?.name || '') : ''}
            />
          )
        ) : isDisabledTab ? (
          <div className="ud-coming-soon-block">
            <div className="ud-coming-soon-icon">🚧</div>
            <h3 className="ud-coming-soon-title">Coming Soon</h3>
            <p className="ud-coming-soon-text">
              This feature is under development. It will allow you to upload bank statements and transaction
              reports to automatically extract data into CSV format for importing into the bookkeeping system.
            </p>
          </div>
        ) : (
          <>
            {/* First-time hint */}
            {showFirstTimeHint && activeTab === 'receipts-docs' && (
              <div className="ud-first-time-hint">
                <p className="ud-first-time-text">
                  IF THIS IS YOUR FIRST TIME UPLOADING FILES HERE, PLEASE UPLOAD A SAMPLE FILE TO TRY FIRST;
                  THEN GO TO PROCESS RECEIPTS AND SEE IF THIS IS WHAT YOU NEED. IF NOT, YOU CAN DELETE THE INDIVIDUAL PAGES THERE.
                </p>
                <button className="ud-dismiss-hint" onClick={() => setShowFirstTimeHint(false)} title="Dismiss">✕</button>
              </div>
            )}

            {/* Owner Selector */}
            <div className="ud-client-section">
              <label className="ud-client-label">Choose Owner of the document(s)</label>
              <SearchableDropdown
                className="ud-client-select"
                options={[
                  ...clients.filter(c => c.type === 'personal').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Account Owners',
                  })),
                  ...clients.filter(c => c.type === 'family').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Family Members',
                  })),
                  ...clients.filter(c => c.type === 'business').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Businesses',
                  })),
                  ...clients.filter(c => c.type === 'client').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Client & Family',
                  })),
                  ...clients.filter(c => c.type === 'client_business').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: "Client's Businesses",
                  })),
                ]}
                value={selectedClient != null ? String(selectedClient) : ''}
                // A client document must always have an owner — clearing falls back to the client.
                onChange={(val) => setSelectedClient(val ? Number(val) : (clientMode && selectedClientId != null ? Number(selectedClientId) : null))}
                placeholder={clientMode ? '-- Select an Owner --' : '-- Optional: Select an Owner --'}
              />
            </div>

            {/* Auto-fill extraction engine */}
            <div className="ud-engine-section">
              <label className="ud-client-label">Auto-fill the Filing tab from the document</label>
              <div className="ud-engine-toggle" role="group" aria-label="Auto-fill engine">
                <button
                  type="button"
                  className={`ud-engine-btn ${engine === 'auto' ? 'active' : ''}`}
                  onClick={() => changeEngine('auto')}
                >
                  Auto
                </button>
                <button
                  type="button"
                  className={`ud-engine-btn ${engine === 'local' ? 'active' : ''}`}
                  onClick={() => changeEngine('local')}
                >
                  Free &middot; Browser
                </button>
                <button
                  type="button"
                  className={`ud-engine-btn ${engine === 'claude' ? 'active' : ''}`}
                  onClick={() => changeEngine('claude')}
                >
                  Claude AI
                </button>
                {process.env.REACT_APP_SELF_HOSTED_EXTRACT === '1' && (
                  <button
                    type="button"
                    className={`ud-engine-btn ${engine === 'self_hosted' ? 'active' : ''}`}
                    onClick={() => changeEngine('self_hosted')}
                  >
                    Self-hosted
                  </button>
                )}
              </div>
              <p className="ud-engine-hint">
                {engine === 'auto' && "Smart & low-cost — reads every document free in your browser first, and sends only the few it can't confidently read (logo-only vendors, scans) to AI. You review everything in the Filing tab."}
                {engine === 'local' && "Free & private — browser-only text + OCR, no AI. Scans and logo-only vendors may extract little. You review everything in the Filing tab."}
                {engine === 'claude' && "Claude AI reads every document (scans, photos, logos) for the most accurate auto-fill — small per-document cost. You review everything in the Filing tab."}
                {engine === 'self_hosted' && "Your self-hosted vision model reads every document — no per-document API cost once your GPU endpoint is configured."}
              </p>
            </div>

            {activeTab === 'receipts-auto' && (
              <label className="ud-review-toggle">
                <input type="checkbox" checked={reviewSplit} onChange={e => changeReviewSplit(e.target.checked)} />
                Let me check the split before the documents are saved
              </label>
            )}

            {/* Drop Zone */}
            <div
              className={`ud-dropzone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_INPUT}
                multiple
                onChange={handleFileInputChange}
                className="ud-file-input"
              />
              <div className="ud-dropzone-icon">{isDragging ? '📥' : '📄'}</div>
              <p className="ud-dropzone-mode">
                {activeTab === 'receipts-separate'
                  ? 'Document will be separated in individual Receipts / Docs before uploading'
                  : activeTab === 'receipts-auto'
                    ? 'Pages that belong together are kept as one document; the rest are separated'
                    : 'Upload documents without pages separation'}
              </p>
              <p className="ud-dropzone-title">
                {isDragging ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="ud-dropzone-subtitle">
                or <span className="ud-browse-link">browse your computer</span>
              </p>
              <p className="ud-dropzone-paste">
                📸 <strong>Screenshot of a transaction?</strong>{' '}
                {IS_MAC
                  ? <>Press <kbd>Cmd</kbd>+<kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>4</kbd> and drag over the area, then come back here and press <kbd>Cmd</kbd>+<kbd>V</kbd>.</>
                  : <>Press <kbd>Win</kbd>+<kbd>Shift</kbd>+<kbd>S</kbd> and drag over the area, then come back here and press <kbd>Ctrl</kbd>+<kbd>V</kbd>.</>}
                {CAN_CAPTURE && (
                  <>
                    <br />
                    <button type="button" className="ud-btn ud-btn-secondary ud-capture-btn" onClick={startCapture}>
                      🖥️ Capture screen
                    </button>
                  </>
                )}
              </p>
              <p className="ud-dropzone-meta">
                PDF, PNG, JPEG/JPG, or an email (.eml / .msg) &middot; Max {formatFileSize(maxFileSize)} per file &middot; Images are converted to PDF &middot; All files are gzip-compressed
                {activeTab === 'receipts-separate' && (
                  <span> &middot; <strong>Each PDF page saved as a separate document</strong></span>
                )}
                {activeTab === 'receipts-auto' && (
                  <span> &middot; <strong>Single-page and multi-page documents separated automatically</strong></span>
                )}
              </p>
            </div>

            {/* Pasted screenshot — on-page message, closed by X or any click */}
            {pasteState && (
              <div className={`ud-paste-toast ${pasteState}`} role="status" aria-live="polite">
                <span className="ud-paste-toast-icon">{pasteState === 'done' ? '✅' : pasteState === 'error' ? '⚠️' : '⏳'}</span>
                <span className="ud-paste-toast-text">
                  {pasteState === 'done'
                    ? <><strong>Your document is uploaded.</strong> You'll find it in Process Docs.</>
                    : pasteState === 'error'
                      ? <><strong>The screenshot could not be uploaded.</strong> {pastedJobs.find(j => j.status === 'error')?.errorMsg}</>
                      : <><strong>Screenshot received</strong> — uploading…</>}
                </span>
                <button type="button" className="ud-paste-toast-close" aria-label="Close" onClick={() => setPastedNames([])}>×</button>
              </div>
            )}

            {/* Files only partly on the server — never left unsaid */}
            {(() => {
              // A file still in the list below is being dealt with there.
              const inList = new Set(allFiles.filter(f => f.status !== 'done').map(f => f.name));
              const shown = incomplete.filter(u => !inList.has(u.original_filename));
              if (shown.length === 0) return null;
              return (
                <div className="ud-incomplete" role="alert">
                  <p className="ud-incomplete-title">
                    {shown.length === 1 ? 'One file was not fully uploaded' : `${shown.length} files were not fully uploaded`}
                  </p>
                  <ul className="ud-incomplete-list">
                    {shown.map(u => (
                      <li key={`${u.source_hash}-${u.upload_source}`}>
                        <strong>{u.original_filename}</strong> &mdash; {u.pages_saved} of {u.pages_total} pages saved
                        {' '}({u.pages_total - u.pages_saved} missing)
                      </li>
                    ))}
                  </ul>
                  <p className="ud-incomplete-help">
                    Add the same file again on the {shown.some(u => u.upload_source === 'receipts-auto') && shown.some(u => u.upload_source !== 'receipts-auto')
                      ? 'tab it was uploaded on'
                      : shown[0].upload_source === 'receipts-auto' ? 'Auto-Separate tab' : 'Separate Receipts tab'}.
                    {' '}Only the missing pages are uploaded &mdash; the pages already saved are not duplicated.
                  </p>
                </div>
              );
            })()}

            {/* File Queue */}
            {files.length > 0 && (
              <div className="ud-file-queue">
                <div className="ud-queue-header">
                  <h3 className="ud-queue-title">
                    Files ({files.length})
                    {queuedCount > 0 && <span className="ud-badge ud-badge-queued">{queuedCount} ready</span>}
                    {uploadingCount > 0 && <span className="ud-badge ud-badge-uploading">{uploadingCount} uploading</span>}
                    {reviewCount > 0 && <span className="ud-badge ud-badge-review">{reviewCount} to check</span>}
                    {doneCount > 0 && <span className="ud-badge ud-badge-done">{doneCount} done</span>}
                    {errorCount > 0 && <span className="ud-badge ud-badge-error">{errorCount} error</span>}
                  </h3>
                  <div className="ud-queue-actions">
                    <button className="ud-btn ud-btn-secondary" onClick={clearAllFiles}>Clear All</button>
                  </div>
                </div>

                {(queuedCount > 0 || uploadingCount > 0) && (
                  <p className="ud-queue-note">
                    Uploads keep running while you work elsewhere in Akountant. If this tab is closed or reloaded before they finish, they continue from where they stopped the next time you open Akountant in this browser.
                  </p>
                )}

                <div className="ud-file-list" ref={fileListRef}>
                  {files.map(f => (
                    <div key={f.id} data-job-id={f.id} className={`ud-file-item ud-file-${f.status}${f.id === currentJobId ? ' ud-file-current' : ''}`}>
                      <div className="ud-file-icon">
                        {f.status === 'done' ? '✅' : f.status === 'error' ? '❌' : f.status === 'uploading' ? '⏳' : f.status === 'review' ? '✂️' : (f.convertedFrom === 'Email' ? '✉️' : f.convertedFrom ? '🖼️' : '📄')}
                      </div>
                      <div className="ud-file-info">
                        <div className="ud-file-name">
                          {f.name}
                          {f.convertedFrom && f.status !== 'done' && (
                            <span className="ud-file-convert-tag">{f.convertedFrom === 'Email' ? 'Email → documents' : `${f.convertedFrom} → PDF`}</span>
                          )}
                          {f.resumed && f.status !== 'done' && (
                            <span className="ud-file-convert-tag" title="This upload was interrupted and has been picked up again. Pages already saved are not uploaded twice.">Resumed</span>
                          )}
                        </div>
                        <div className="ud-file-meta">
                          {formatFileSize(f.size)}
                          {f.storedSize != null && f.storedSize > 0 && f.storedSize < f.size && (
                            <span className="ud-file-compressed">
                              {' '}&rarr; {formatFileSize(f.storedSize)} compressed ({Math.round((1 - f.storedSize / f.size) * 100)}% saved)
                            </span>
                          )}
                          {f.pagesCreated != null && f.pagesCreated > 0 && (
                            <span className="ud-file-pages-msg">
                              {' '}&mdash; {f.pagesCreated} {f.pagesCreated === 1 ? 'page' : 'pages'} separated and saved
                            </span>
                          )}
                          {f.status === 'done' && !!f.alreadyUploaded && (
                            <span className="ud-file-pages-msg">
                              {' '}&mdash; {(f.pagesCreated || f.docsCreated)
                                ? `${f.alreadyUploaded} ${f.alreadyUploaded === 1 ? 'page was' : 'pages were'} already uploaded and not saved again`
                                : `already uploaded: all ${f.alreadyUploaded} ${f.alreadyUploaded === 1 ? 'page is' : 'pages are'} in your documents, nothing saved again`}
                            </span>
                          )}
                          {f.status === 'review' && f.split && (
                            <span className="ud-file-review-msg">
                              {' '}&mdash; {f.split.pageCount} pages read, {f.split.groups.length} {f.split.groups.length === 1 ? 'document' : 'documents'} suggested. Not saved yet.
                            </span>
                          )}
                          {f.docsCreated != null && f.docsCreated > 0 && f.emailDocs == null && (
                            <span className="ud-file-pages-msg">
                              {' '}&mdash; {f.pagesTotal} {f.pagesTotal === 1 ? 'page' : 'pages'} saved as {f.docsCreated} {f.docsCreated === 1 ? 'document' : 'documents'}
                            </span>
                          )}
                          {f.convertedFrom && f.convertedFrom !== 'Email' && f.status === 'done' && (
                            <span className="ud-file-converted-msg"> &mdash; Converted from {f.convertedFrom} to PDF</span>
                          )}
                          {f.emailDocs != null && (
                            <span className="ud-file-pages-msg">
                              {' '}&mdash; {f.emailDocs} {f.emailDocs === 1 ? 'document' : 'documents'} saved from this email
                            </span>
                          )}
                          {f.emailSkipped != null && f.emailSkipped.length > 0 && (
                            <span className="ud-file-error-msg">
                              {' '}&mdash; not filed (only PDF, PNG and JPEG are): {f.emailSkipped.join(', ')}
                            </span>
                          )}
                          {f.errorMsg && <span className="ud-file-error-msg"> &mdash; {f.errorMsg}</span>}
                          {f.status === 'done' && !f.errorMsg && !f.pagesCreated && f.docsCreated == null && f.emailDocs == null && !f.alreadyUploaded && (
                            <span className="ud-file-done-msg"> &mdash; Saved to dms_documents</span>
                          )}
                        </div>
                        {f.status === 'uploading' && (
                          <div className="ud-progress-bar">
                            <div className="ud-progress-fill" style={{ width: `${f.progress}%` }} />
                          </div>
                        )}
                      </div>
                      {f.status === 'review' && (
                        <button
                          type="button"
                          className="ud-btn ud-btn-primary ud-file-review-btn"
                          onClick={(e) => { e.stopPropagation(); setReviewJobId(f.id); }}
                        >
                          Check split
                        </button>
                      )}
                      {f.status === 'error' && f.ctx && (
                        <button
                          type="button"
                          className="ud-btn ud-btn-primary ud-file-review-btn"
                          title="Try again. Pages already saved are not uploaded twice."
                          onClick={(e) => { e.stopPropagation(); retryUploadJob(f.id); }}
                        >
                          Retry
                        </button>
                      )}
                      <button
                        className="ud-file-remove"
                        onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {queuedCount > 0 && (
              <div className="ud-notes-section">
                <label className="ud-notes-label">Notes (optional)</label>
                <textarea
                  className="ud-notes-input"
                  placeholder="Add any notes about these uploads..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                />
              </div>
            )}

            {/* Previously Uploaded Documents for this tab */}
            {tabDocs.length > 0 && (
              <div className="ud-history">
                <h3 className="ud-history-title">Uploaded Documents ({tabDocs.length})</h3>
                <div className="ud-history-table-wrap">
                <table className="ud-history-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th className="ud-sortable" onClick={() => toggleSort('filename')}>File Name{sortArrow('filename')}</th>
                      {activeTab !== 'receipts-separate' && (
                        <th className="ud-sortable" onClick={() => toggleSort('page_count')}>Pages{sortArrow('page_count')}</th>
                      )}
                      <th className="ud-sortable" onClick={() => toggleSort('upload_date')}>Uploaded{sortArrow('upload_date')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabDocs.map((d, idx) => {
                      const isVirtual = !!d.__virtual;
                      const isOriginal = activeTab === 'receipts-separate' && d.filename === d.original_filename;
                      const isPage = activeTab === 'receipts-separate' && d.filename !== d.original_filename;
                      return (
                        <tr key={d.document_id} className={isOriginal ? 'ud-row-original' : isPage ? 'ud-row-page' : ''}>
                          <td className="ud-history-id">{idx + 1}</td>
                          <td className="ud-history-name">
                            <span className="ud-history-icon">{isOriginal ? '📋' : '📄'}</span>
                            {isVirtual ? (
                              <span className="ud-history-name-plain" title="Multi-page document — delete removes all its pages">
                                {d.filename}
                                <span className="ud-page-count-hint"> ({d.page_count} page{d.page_count !== 1 ? 's' : ''})</span>
                              </span>
                            ) : (
                            <span
                              className="ud-history-name-link"
                              onClick={() => goToProcessDoc(d.document_id)}
                              title="Click to open in Process Docs / Receipts"
                            >
                              {isPage && <span className="ud-page-indent">↳ </span>}
                              {d.filename}
                            </span>
                            )}
                          </td>
                          {activeTab !== 'receipts-separate' && (
                            <td>{d.page_count ?? '—'}</td>
                          )}
                          <td>{formatDate(d.upload_date)}</td>
                          <td>
                            <button
                              className="ud-history-delete"
                              onClick={(e) => { e.stopPropagation(); requestDelete(d); }}
                              title={isVirtual ? 'Delete this document & all its pages' : isOriginal ? 'Delete original & all separated pages' : 'Delete'}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Auto-separate: check a file's suggested split */}
      {captureFrame && (
        <CaptureScreenModal
          frame={captureFrame}
          onUse={keepCapture}
          onRetake={() => { void startCapture(); }}
          onClose={() => setCaptureFrame(null)}
        />
      )}

      {reviewJob && (
        <SplitReviewModal
          job={reviewJob}
          onClose={() => setReviewJobId(null)}
          onConfirm={(groups) => { confirmUploadSplit(reviewJob.id, groups); setReviewJobId(null); }}
        />
      )}

      {/* Big, bold delete-confirmation warning */}
      {deleteTarget && (() => {
        const dt = deleteTarget;
        const n = dt.childDocs.length;
        const isCascade = n > 0;                       // deleting a parent + its pages
        const total = (dt.doc.__virtual ? 0 : 1) + n;  // total docs that get deleted
        return (
          <div className="ud-delete-overlay" onClick={() => setDeleteTarget(null)}>
            <div className="ud-delete-modal" onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true">
              <div className="ud-delete-icon">⚠️</div>
              <div className="ud-delete-title">Delete this document?</div>
              <div className="ud-delete-body">
                {isCascade ? (
                  <p>
                    You are about to delete the multi-page document <strong>“{dt.doc.original_filename}”</strong> and all{' '}
                    <strong>{n}</strong> separated page{n !== 1 ? 's' : ''}.<br />
                    <strong className="ud-delete-emphasis">This permanently removes them from the system — forever.</strong>
                  </p>
                ) : (
                  <p>
                    You are about to delete <strong>“{dt.doc.filename}”</strong>.<br />
                    <strong className="ud-delete-emphasis">This removes it from the system.</strong>
                  </p>
                )}
              </div>
              <div className="ud-delete-actions">
                <button className="ud-delete-cancel-btn" onClick={() => setDeleteTarget(null)}>Cancel</button>
                <button className="ud-delete-confirm-btn" onClick={confirmDelete}>
                  {isCascade ? `Delete all ${total}` : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default UploadDocsPage;
